import { Validators } from '@angular/forms';
import { isValid, set } from 'date-fns';

import { InteractionDto, NoteCategory } from '../../coaching-base/general';
import {
    createNoteDetail,
    noteDetailFormValueReverseTransformer,
    noteDetailFormValueTransformer,
} from '../../coaching-note/note-detail.form';
import { TranslationKeyMap } from '../coaching-observation.i18n';
import { CmpsFormField, CmpsFormFields } from '../experimental.form';
import {
    createScore,
    scoreFormValueReverseTransformer,
    scoreFormValueTransformer,
} from '../observation-score/score.observation.form';
import { CoachingInteractionModel } from '../observation/observation.model';

import { InteractionFormValue } from './interaction-form-value.model';

export interface InteractionFormFields extends CmpsFormFields {
    TRACKING_ID: CmpsFormField;
    TRACKING_TIME: CmpsFormField;
    TRACKING_CHANGED: CmpsFormField;
    TRACKING_ORIGINAL: CmpsFormField;
    INTERACTION_ID: CmpsFormField;
    INTERACTION_TYPE: CmpsFormField;
    INTERACTION_PURPOSE_ID: CmpsFormField;
    IDENTIFIER: CmpsFormField;
    TIME: CmpsFormField;
    DATE: CmpsFormField;
    QUEUE: CmpsFormField;
    NOTES: CmpsFormField;
    SCORING: CmpsFormField;
}

// eslint-disable-next-line @typescript-eslint/no-redeclare
export const InteractionFormFields: InteractionFormFields = {
    TRACKING_ID: {
        name: 'trackingId',
        label: undefined,
    },
    TRACKING_TIME: {
        name: 'trackingTime',
        label: undefined,
    },
    TRACKING_CHANGED: {
        name: 'trackingChanged',
        label: undefined,
    },
    TRACKING_ORIGINAL: {
        name: 'trackingOriginal',
        label: undefined,
    },
    INTERACTION_ID: {
        name: 'interactionId',
        label: '',
    },
    INTERACTION_TYPE: {
        name: 'interactionType',
        label: TranslationKeyMap._7349_INTERACTION_TYPE,
        validators: [Validators.required],
        validationMsgs: {
            required:
                TranslationKeyMap._640__FIELD_LABEL__IS_A_REQUIRED_FIELD__PLEASE_ENTER_A__FIELD_LABEL_,
        },
    },
    INTERACTION_PURPOSE_ID: {
        name: 'interactionPurposeId',
        label: '',
    },
    IDENTIFIER: {
        name: 'identifier',
        label: TranslationKeyMap._9652_INTERACTION_REF,
        validators: [Validators.maxLength(30)],
        validationMsgs: {
            maxlength:
                TranslationKeyMap._7227__FIELD_LABEL__MUST_BE_LESS_THAN_OR_EQUAL_TO__REQUIREDLENGTH__CHARACTERS_,
        },
    },
    TIME: {
        name: 'time',
        label: TranslationKeyMap._9703_INTERACTION_TIME,
        validators: [Validators.required],
        validationMsgs: {
            required:
                TranslationKeyMap._640__FIELD_LABEL__IS_A_REQUIRED_FIELD__PLEASE_ENTER_A__FIELD_LABEL_,
            dateInvalid: TranslationKeyMap._9734_INVALID_TIME_,
            dateMax:
                TranslationKeyMap._9735__FIELD_LABEL__CANNOT_BE_IN_THE_FUTURE_,
        },
    },
    DATE: {
        name: 'date',
        label: TranslationKeyMap._965_DATE,
        validators: [Validators.required],
        validationMsgs: {
            required:
                TranslationKeyMap._640__FIELD_LABEL__IS_A_REQUIRED_FIELD__PLEASE_ENTER_A__FIELD_LABEL_,
            ngbDate: TranslationKeyMap._9512_INVALID_DATE_,
        },
    },
    QUEUE: {
        name: 'queue',
        label: '',
    },
    NOTES: {
        name: 'notes',
        label: '',
    },
    SCORING: {
        name: 'scoring',
        label: '',
    },
};

export const defaultQueueId = -9000;

export function createInteraction(
    interactionTypeId?: number,
    currentScoringConfigId?: number,
): InteractionFormValue {
    // the time form control will intentionally zero these out. prevent an initial tracking change
    const now = set(new Date(), { seconds: 0, milliseconds: 0 });
    return {
        trackingId: Math.random(),
        trackingTime: Date.now(),
        trackingChanged: false,
        trackingOriginal: null,
        interactionId: undefined,
        interactionType: interactionTypeId || null, // '|| null' 'cause of trackingChanged compare
        interactionPurposeId: null,
        identifier: null,
        time: now,
        date: now,
        queue: defaultQueueId,
        notes: [createNoteDetail(NoteCategory.GENERAL)],
        scoring: [createScore(currentScoringConfigId)],
    };
}

export function interactionDateTimeTransformer(date: Date, time: Date): Date {
    const dDate = !isValid(date) ? new Date() : date;
    const tDate = !isValid(time) ? new Date() : time;
    return new Date(
        dDate.getFullYear(),
        dDate.getMonth(),
        dDate.getDate(),
        tDate.getHours(),
        tDate.getMinutes(),
        0,
        0,
    );
}

// TODO move?
export function interactionFormValueTransformer(
    model: InteractionDto,
): InteractionFormValue {
    if (!model) {
        return null;
    }
    return {
        trackingId: Math.random(),
        trackingTime: Date.now(),
        trackingChanged: false,
        trackingOriginal: model,
        interactionId: model.interactionId,
        interactionType: model.interactionTypeId,
        interactionPurposeId: model.interactionPurposeId,
        identifier: model.interactionIdentifier,
        // the time form control will intentionally zero these out. prevent an initial tracking change
        time: set(model.interactionDateTime, { seconds: 0, milliseconds: 0 }),
        date: model.interactionDateTime,
        queue: model.queueId != null ? model.queueId : defaultQueueId,
        notes: (model.notes || []).map(noteDetailFormValueTransformer),
        scoring: !model.scoring
            ? []
            : [model.scoring].map(scoreFormValueTransformer),
    };
}

// TODO move?
export function interactionFormValueReverseTransformer(
    model: InteractionFormValue,
): CoachingInteractionModel {
    return {
        interactionId: model.interactionId,
        interactionIdentifier: model.identifier,
        interactionTypeId: model.interactionType,
        interactionPurposeId: model.interactionPurposeId,
        performerId: undefined,
        observerId: undefined,
        interactionDateTime: interactionDateTimeTransformer(
            model.date,
            model.time,
        ),
        queueId: model.queue !== defaultQueueId ? model.queue : null,
        createdBy: undefined,
        createdOn: undefined,
        notes: (model.notes || []).map(noteDetailFormValueReverseTransformer),
        scoring: (model.scoring || []).map(scoreFormValueReverseTransformer),
    };
}
